import { useEffect } from 'react';
import { BulkEditStatusPopover } from 'src/components/common/BulkActions/BulkEditStatusPopover';
import { MultiSelectionToggleGlobal } from 'src/components/common/MultiSelect/Toggle/MultiSelectionToggleGlobal';
import { FilterToolbar } from 'src/components/FilterToolbar';
import { useFiltersHelpers } from 'src/components/FilterToolbar/useFiltersHelpers';
import { ViewModeSelector } from 'src/components/MainFilterBar';
import * as styles from 'src/components/MainFilterBar/MainFilterBar.css';
import {
  purchaseMandatoryFiltersToShow,
  usePurchaseFilters,
} from 'src/components/MainFilterBar/usePurchaseFilters';
import { PurchaseGlobalActionsPermissionsWrapper } from 'src/components/Purchases/PurchaseFilterBar/PurchaseGlobalActionsPermissionsWrapper';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useFilterToolbarQuickFilters } from 'src/hooks/useQuickFilters';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { PURCHASE_VIEW_MODE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  ActionOutboxEntityType,
  PurchaseOrderQuery,
  PurchaseViewMode,
  UserSetting,
} from 'src/WebApiController';

import { useDefaultPurchaseFilters } from './hooks/useDefaultPurchaseFilters';
import { PURCHASE_VIEW_MODE_TO_ICON } from './PurchaseFilterBar.types';
import { PurchaseSearchBox } from './PurchaseSearchBox';

export function PurchaseFilterBar({
  isEventPage = false,
  returnUrl,
  viagVirtId,
}: {
  isEventPage?: boolean;
  returnUrl?: string;
  viagVirtId?: string;
}) {
  const {
    filterQuery: purchaseQuery,
    initialQuery,
    tempQuery,
    setFilterQuery,
    setInitialQuery,
    setTempQuery,
    resetTempQuery,
  } = useFilterQueryContext<PurchaseOrderQuery>();

  useEffect(() => {
    setTempQuery(purchaseQuery);
  }, [purchaseQuery, setTempQuery]);

  const defaultQuickFilters = useDefaultPurchaseFilters({
    purchaseQuery,
    initialQuery,
  });

  const { value: defaultViewModeUserSetting, setUserSetting } =
    useServerUserSetting<PurchaseViewMode>({
      id: UserSetting.PurchasePageViewMode,
      currentLoginUserOnly: true,
    });

  const filterToolbarProps = useFilterToolbarQuickFilters({
    quickFiltersStateSetting: UserSetting.QuickFiltersStatePurchases,
    customQuickFiltersSetting: UserSetting.QuickFiltersCustomPurchases,
    defaultQuickFilters,
    currentQuery: purchaseQuery,
    initialQuery,
    onSelect: ({ id, query }) => {
      if (query.viewMode != null) {
        // We need to update initialQuery, otherwise the quick filter "All"
        // may change the current view mode
        if (query.viewMode !== initialQuery.viewMode) {
          setInitialQuery({ ...initialQuery, viewMode: query.viewMode });
        }
      }
      setFilterQuery(query);

      // This needs to happen after to reduce re-rendering
      if (query.viewMode != null) {
        // Update user setting view mode too so it won't revert the filter query viewMode
        if (query.viewMode !== defaultViewModeUserSetting) {
          setUserSetting(query.viewMode);
        }
      }
    },
  });

  const isMobile = useMatchMedia('mobile');

  const purchaseFilters = usePurchaseFilters({});

  const { appliedFilters } = useFiltersHelpers({
    filters: purchaseFilters,
    activeQuery: purchaseQuery,
    mandatoryFiltersToShow: purchaseMandatoryFiltersToShow,
    initialQuery,
  });

  return (
    <div className={styles.mainFilterBarDiv}>
      <div className={styles.dockLeftDiv}>
        <div className={styles.mainFilterBarLeftChild}>
          {purchaseQuery && (
            <FilterToolbar
              {...filterToolbarProps}
              filterAppliedCounts={appliedFilters.length}
              onSubmitFilter={() => {
                setFilterQuery(tempQuery);
              }}
              mandatoryFiltersToShow={purchaseMandatoryFiltersToShow}
              filters={purchaseFilters}
              filterAppliedIds={appliedFilters}
              resetTempQuery={resetTempQuery}
              setTempQuery={(partialQuery) => {
                setTempQuery({
                  ...tempQuery,
                  ...partialQuery,
                } as PurchaseOrderQuery);
              }}
              tempQuery={tempQuery}
            />
          )}
        </div>
      </div>
      <div className={styles.dockRightDiv}>
        <div className={styles.mainFilterBarRightChild}>
          {!isMobile && (
            <>
              {!isEventPage && (
                <>
                  <BulkEditStatusPopover
                    entityType={ActionOutboxEntityType.Purchase}
                  />
                  <ViewModeSelector
                    query={purchaseQuery}
                    entityType={ActionOutboxEntityType.Purchase}
                    initialQuery={initialQuery}
                    setFilterQuery={setFilterQuery}
                    setInitialQuery={setInitialQuery}
                    viewModeSettingsId={UserSetting.PurchasePageViewMode}
                    viewModeCidMap={PURCHASE_VIEW_MODE_TO_CID}
                    viewModeIconMap={PURCHASE_VIEW_MODE_TO_ICON}
                  />
                  <PurchaseGlobalActionsPermissionsWrapper>
                    <MultiSelectionToggleGlobal />
                  </PurchaseGlobalActionsPermissionsWrapper>
                </>
              )}

              {isEventPage && <PurchaseSearchBox isEventPage={isEventPage} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
